import { useEffect, useMemo, useState } from "react";
import {
  AlertTriangle, ArrowDownRight, ArrowUpRight, ChevronLeft, ChevronRight, Clock,
  Download, Euro, FileText, Gauge, Info, Search, Timer, TrendingUp, Users, Wallet, X,
} from "lucide-react";
import {
  CartesianGrid, Cell, ComposedChart, Area, Line, Pie, PieChart, Tooltip as RTooltip,
  XAxis, YAxis,
} from "recharts";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { SafeChart } from "@/components/SafeChart";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { eur } from "@/components/accounting/docUtils";
import { fmtDur, timeCategoryLabel } from "@/lib/constants";

const PERIODS = [
  { id: "month", label: "Mois courant" },
  { id: "last_month", label: "Mois précédent" },
  { id: "3m", label: "3 derniers mois" },
  { id: "6m", label: "6 derniers mois" },
  { id: "year", label: "Année en cours" },
  { id: "custom", label: "Période personnalisée" },
];
const iso = (d) => d.toISOString().slice(0, 10);
function rangeFor(period, custom) {
  const now = new Date();
  const today = iso(now);
  switch (period) {
    case "month": return { from: `${today.slice(0, 7)}-01`, to: today };
    case "last_month": {
      const d = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      return { from: iso(d), to: iso(new Date(now.getFullYear(), now.getMonth(), 0)) };
    }
    case "3m": { const d = new Date(now); d.setMonth(d.getMonth() - 3); return { from: iso(d), to: today }; }
    case "6m": { const d = new Date(now); d.setMonth(d.getMonth() - 6); return { from: iso(d), to: today }; }
    case "year": return { from: `${now.getFullYear()}-01-01`, to: today };
    case "custom": return { from: custom.from || today, to: custom.to || today };
    default: return { from: today, to: today };
  }
}
const fmtDay = (d) => new Date(d + "T12:00:00").toLocaleDateString("fr-FR", { day: "numeric", month: "short", year: "numeric" });

const STATUS = {
  rentable: { label: "Rentable", cls: "bg-emerald-50 text-emerald-600 border-emerald-200" },
  surveiller: { label: "À surveiller", cls: "bg-orange-50 text-orange-500 border-orange-200" },
  non_rentable: { label: "Non rentable", cls: "bg-rose-50 text-rose-500 border-rose-200" },
  none: { label: "—", cls: "bg-neutral-50 text-neutral-400 border-neutral-200" },
};
const initialsOf = (name = "") => name.split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]).join("").toUpperCase() || "·";
const AVATAR_BG = ["bg-[#DBEAFE] text-[#1D4ED8]", "bg-[#FEF3C7] text-[#B45309]", "bg-[#EDE9FE] text-[#6D28D9]", "bg-[#DCFCE7] text-[#15803D]", "bg-[#FCE7F3] text-[#BE185D]"];
const avatarCls = (name = "") => AVATAR_BG[(name.charCodeAt(0) || 0) % AVATAR_BG.length];
const rateColor = (rate, seuil, watchUntil) =>
  rate == null ? "text-neutral-300" : rate < seuil ? "text-rose-500" : rate < watchUntil ? "text-orange-500" : "text-emerald-600";

function VarBadge({ value, invert }) {
  if (value == null) return <span className="text-neutral-300">—</span>;
  if (value === 0) return <span className="text-neutral-400 font-semibold">= 0 %</span>;
  const up = value > 0;
  const good = invert ? !up : up;
  const Icon = up ? ArrowUpRight : ArrowDownRight;
  return (
    <span className={`inline-flex items-center gap-0.5 font-semibold ${good ? "text-emerald-600" : "text-rose-500"}`}>
      <Icon className="w-3 h-3" /> {Math.abs(value)} %
    </span>
  );
}

function KpiCard({ id, label, icon: Icon, bg, fg, value, sub, subText }) {
  return (
    <div className="bg-white border border-[#E2E8F0] rounded-2xl shadow-sm p-3" data-testid={`rent-kpi-${id}`}>
      <div className="flex items-center gap-2">
        <span className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0" style={{ background: bg }}>
          <Icon className="w-4 h-4" style={{ color: fg }} />
        </span>
        <p className="text-[10px] font-medium text-neutral-500 leading-tight flex-1">{label}</p>
      </div>
      <p className="font-heading text-xl font-bold mt-1 leading-tight" data-testid={`rent-kpi-${id}-value`}>{value}</p>
      <p className="text-[9px] mt-0.5">{sub} <span className="text-neutral-400">{subText}</span></p>
    </div>
  );
}

export default function RentabiliteTab() {
  const [period, setPeriod] = useState("month");
  const [custom, setCustom] = useState({ from: "", to: "" });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [page, setPage] = useState(1);
  const [detailId, setDetailId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [exportOpen, setExportOpen] = useState(false);

  const { from, to } = rangeFor(period, custom);

  useEffect(() => {
    setLoading(true);
    api.get(`/profitability?from_date=${from}&to_date=${to}`)
      .then((r) => setData(r.data))
      .catch((e) => toast.error(apiError(e)))
      .finally(() => setLoading(false));
  }, [from, to]);

  useEffect(() => {
    if (!detailId) { setDetail(null); return; }
    api.get(`/profitability/client/${detailId}?from_date=${from}&to_date=${to}`)
      .then((r) => setDetail(r.data)).catch((e) => toast.error(apiError(e)));
  }, [detailId, from, to]);

  useEffect(() => { setPage(1); }, [q, statusFilter, from, to]);

  const seuil = data?.seuil ?? 60;
  const watchUntil = data?.watch_until ?? seuil * 7 / 6;
  const k = data?.kpi;

  const rows = useMemo(() => (data?.rows || []).filter((r) => {
    if (statusFilter && r.status !== statusFilter) return false;
    if (q.trim() && !r.client_name.toLowerCase().includes(q.trim().toLowerCase())) return false;
    return true;
  }), [data, q, statusFilter]);
  const PAGE_SIZE = 6;
  const pageCount = Math.max(1, Math.ceil(rows.length / PAGE_SIZE));
  const pageRows = rows.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const rep = data?.repartition;
  const donut = rep ? [
    { id: "rec", label: "Temps bloqué récurrent", sec: rep.recurring_sec, color: "#60A5FA" },
    { id: "chrono", label: "Temps chrono", sec: rep.chrono_sec, color: "#A78BFA" },
    { id: "nf", label: "Temps non facturable", sec: rep.non_billable_sec, color: "#FCD9A8" },
  ].filter((d) => d.sec > 0) : [];

  const exportFile = async (fmt) => {
    setExportOpen(false);
    try {
      const r = await api.get(`/profitability/export?format=${fmt}&from_date=${from}&to_date=${to}`, { responseType: "blob" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(new Blob([r.data]));
      a.download = `rentabilite-${from}_${to}.${fmt}`;
      a.click();
      toast.success(fmt === "pdf" ? "PDF exporté" : "CSV exporté");
    } catch (e) { toast.error(`Export impossible : ${apiError(e)}`); }
  };

  const kpis = k ? [
    { id: "ca", label: "CA facturé du mois", icon: Euro, bg: "#E8F0FE", fg: "#1D4ED8", value: eur(k.ca), sub: <VarBadge value={k.ca_var} />, subText: "vs période précédente" },
    { id: "temps", label: "Temps total comptabilisé", icon: Clock, bg: "#E0F2F1", fg: "#0F766E", value: fmtDur(k.total_sec), sub: <VarBadge value={k.total_sec_var} invert />, subText: "vs période précédente" },
    { id: "taux", label: "Taux horaire moyen", icon: Gauge, bg: "#EDE9FE", fg: "#6D28D9", value: k.avg_rate != null ? `${k.avg_rate.toLocaleString("fr-FR")} €/h` : "—", sub: <VarBadge value={k.avg_rate_var} />, subText: "vs période précédente" },
    { id: "rentables", label: "Clients rentables", icon: Users, bg: "#DCFCE7", fg: "#15803D", value: k.rentables, sub: <span className="text-emerald-600 font-semibold">≥ {(seuil * 7 / 6).toFixed(0)} €/h</span>, subText: "" },
    { id: "surveiller", label: "Clients à surveiller", icon: AlertTriangle, bg: "#FEF3E2", fg: "#B45309", value: k.surveiller, sub: <span className="text-orange-500 font-semibold">{seuil} – {(seuil * 7 / 6).toFixed(0)} €/h</span>, subText: "" },
    { id: "globale", label: "Rentabilité globale", icon: TrendingUp, bg: "#FCE7F3", fg: "#BE185D", value: k.rentabilite_pct != null ? `${k.rentabilite_pct >= 0 ? "+" : ""}${k.rentabilite_pct} %` : "—", sub: <VarBadge value={k.rentabilite_pct != null && k.rentabilite_prev_pct != null ? k.rentabilite_pct - k.rentabilite_prev_pct : null} />, subText: "vs période précédente" },
  ] : [];

  return (
    <div className="flex flex-col gap-3 pt-3 pb-4" data-testid="rentabilite-tab">
      {/* Barre période + export */}
      <div className="flex items-center gap-2 shrink-0 flex-wrap">
        <p className="text-xs text-neutral-500 flex items-center gap-1.5" data-testid="rent-seuil-info">
          <Info className="w-3.5 h-3.5 text-neutral-400" />
          Seuil DGA : <strong className="text-neutral-700">{seuil} €/h</strong>
          <span className="text-neutral-400" title="Le seuil horaire minimum est défini dans les paramètres agence et sert de référence pour calculer la rentabilité client.">(modifiable dans Paramètres)</span>
        </p>
        <div className="ml-auto flex items-center gap-2">
          {period === "custom" && (
            <>
              <input type="date" value={custom.from} onChange={(e) => setCustom((c) => ({ ...c, from: e.target.value }))}
                className="text-xs border border-neutral-200 rounded-lg px-2 py-1.5 bg-white" data-testid="rent-custom-from" />
              <input type="date" value={custom.to} onChange={(e) => setCustom((c) => ({ ...c, to: e.target.value }))}
                className="text-xs border border-neutral-200 rounded-lg px-2 py-1.5 bg-white" data-testid="rent-custom-to" />
            </>
          )}
          <select value={period} onChange={(e) => setPeriod(e.target.value)} data-testid="rent-period"
            className="text-xs font-semibold text-neutral-600 border border-neutral-200 rounded-lg px-2.5 py-1.5 bg-white focus:outline-none cursor-pointer">
            {PERIODS.map((p) => <option key={p.id} value={p.id}>{p.label}</option>)}
          </select>
          <span className="text-[11px] text-neutral-400 whitespace-nowrap" data-testid="rent-range-label">{fmtDay(from)} – {fmtDay(to)}</span>
          <div className="relative">
            <button onClick={() => setExportOpen((v) => !v)} data-testid="rent-export-btn"
              className="inline-flex items-center gap-1.5 bg-[#0B132B] hover:bg-[#1C2A4F] text-white text-xs font-semibold rounded-lg px-3 py-1.5 transition-colors">
              <Download className="w-3.5 h-3.5" /> Exporter
            </button>
            {exportOpen && (
              <>
                <div className="fixed inset-0 z-30" onClick={() => setExportOpen(false)} />
                <div className="absolute right-0 top-8 z-40 w-44 bg-white border border-neutral-200 rounded-xl shadow-lg py-1" data-testid="rent-export-menu">
                  <button onClick={() => exportFile("pdf")} data-testid="rent-export-pdf" className="w-full flex items-center gap-2 px-3 py-2 text-xs text-left hover:bg-neutral-50">
                    <FileText className="w-3.5 h-3.5 text-neutral-400" /> PDF
                  </button>
                  <button onClick={() => exportFile("csv")} data-testid="rent-export-csv" className="w-full flex items-center gap-2 px-3 py-2 text-xs text-left hover:bg-neutral-50">
                    <Download className="w-3.5 h-3.5 text-neutral-400" /> CSV / Excel
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* KPI */}
      <div className="grid grid-cols-2 md:grid-cols-3 2xl:grid-cols-6 gap-2.5 shrink-0" data-testid="rent-kpis">
        {kpis.map((c) => <KpiCard key={c.id} {...c} />)}
      </div>

      {/* Tableau central */}
      <div className="shrink-0 flex flex-col bg-white border border-[#E2E8F0] rounded-2xl shadow-sm overflow-hidden max-h-[380px]" data-testid="rent-table-card">
        <div className="flex items-center gap-3 px-4 pt-3 pb-2 shrink-0 flex-wrap">
          <div className="min-w-52">
            <h3 className="font-heading font-bold text-sm flex items-center gap-2"><Gauge className="w-4 h-4 text-neutral-400" /> Rentabilité par client</h3>
            <p className="text-[10px] text-neutral-400">Comparaison du budget facturé et du temps passé pour évaluer la rentabilité de chaque client.</p>
          </div>
          <div className="ml-auto flex items-center gap-2">
            <div className="relative">
              <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-neutral-400" />
              <input className="pl-8 h-8 text-xs rounded-lg border border-neutral-200 w-52 focus:outline-none focus:ring-1 focus:ring-[#1D4ED8]"
                placeholder="Rechercher un client…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="rent-search" />
            </div>
            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} data-testid="rent-status-filter"
              className="text-xs border border-neutral-200 rounded-lg px-2 py-1.5 bg-white focus:outline-none cursor-pointer">
              <option value="">Tous les clients</option>
              <option value="rentable">Rentables</option>
              <option value="surveiller">À surveiller</option>
              <option value="non_rentable">Non rentables</option>
            </select>
          </div>
        </div>
        <div className="flex-1 min-h-0 overflow-auto px-2" data-testid="rent-table-wrap">
          <table className="w-full text-sm min-w-[900px]">
            <thead className="sticky top-0 bg-white z-10">
              <tr className="text-left text-[10px] uppercase tracking-wider text-neutral-400 border-b border-neutral-100">
                <th className="px-2 py-2 font-semibold">Client</th>
                <th className="px-2 py-2 font-semibold text-right">Budget mensuel facturé</th>
                <th className="px-2 py-2 font-semibold text-right" title="Temps planifié au contrat (comparatif, non additionné au chrono)">Temps bloqué récurrent</th>
                <th className="px-2 py-2 font-semibold text-right">Temps chrono</th>
                <th className="px-2 py-2 font-semibold text-right" title="Temps total = temps chrono (le récurrent sert de comparaison, sans double comptage)">Temps total</th>
                <th className="px-2 py-2 font-semibold text-right">Taux horaire réel</th>
                <th className="px-2 py-2 font-semibold text-right">Seuil DGA</th>
                <th className="px-2 py-2 font-semibold">Statut</th>
                <th className="px-2 py-2 font-semibold text-right">Marge / écart</th>
              </tr>
            </thead>
            <tbody>
              {!loading && pageRows.length === 0 && (
                <tr><td colSpan={9} className="px-4 py-8 text-center text-xs text-neutral-400" data-testid="rent-empty">
                  Aucune donnée de facturation ou de temps sur cette période.
                </td></tr>
              )}
              {pageRows.map((r) => {
                const st = STATUS[r.status] || STATUS.none;
                return (
                  <tr key={r.client_id} onClick={() => setDetailId(r.client_id)} data-testid={`rent-row-${r.client_id}`}
                    className="border-b border-neutral-50 last:border-0 hover:bg-neutral-50/70 cursor-pointer transition-colors">
                    <td className="px-2 py-2">
                      <span className="flex items-center gap-2">
                        <span className={`w-6 h-6 rounded-full text-[8px] font-bold flex items-center justify-center shrink-0 ${avatarCls(r.client_name)}`}>{initialsOf(r.client_name)}</span>
                        <span className="text-xs font-medium text-neutral-700 truncate max-w-40">{r.client_name}</span>
                      </span>
                    </td>
                    <td className="px-2 py-2 text-xs font-semibold text-right whitespace-nowrap">{eur(r.invoiced)}</td>
                    <td className="px-2 py-2 text-xs text-neutral-500 text-right whitespace-nowrap">{r.recurring_sec ? fmtDur(r.recurring_sec) : "—"}</td>
                    <td className="px-2 py-2 text-xs text-neutral-500 text-right whitespace-nowrap">{r.chrono_sec ? fmtDur(r.chrono_sec) : "—"}</td>
                    <td className="px-2 py-2 text-xs font-medium text-right whitespace-nowrap">{r.total_sec ? fmtDur(r.total_sec) : "—"}</td>
                    <td className={`px-2 py-2 text-xs font-bold text-right whitespace-nowrap ${rateColor(r.rate, seuil, watchUntil)}`} data-testid={`rent-rate-${r.client_id}`}>
                      {r.rate != null ? `${Math.round(r.rate)} € / h` : "—"}
                    </td>
                    <td className="px-2 py-2 text-xs text-neutral-400 text-right whitespace-nowrap">≥ {seuil} €</td>
                    <td className="px-2 py-2"><span className={`text-[10px] font-semibold border rounded-full px-2 py-0.5 whitespace-nowrap ${st.cls}`} data-testid={`rent-status-${r.client_id}`}>{st.label}</span></td>
                    <td className={`px-2 py-2 text-xs font-bold text-right whitespace-nowrap ${r.marge >= 0 ? "text-emerald-600" : "text-rose-500"}`} data-testid={`rent-marge-${r.client_id}`}>
                      {r.marge >= 0 ? "+ " : "− "}{eur(Math.abs(r.marge))}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div className="flex items-center gap-2 px-4 py-2 border-t border-neutral-100 shrink-0" data-testid="rent-pagination">
          <p className="text-[11px] text-neutral-400">{rows.length} client(s)</p>
          <div className="ml-auto flex items-center gap-1">
            <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page <= 1} data-testid="rent-page-prev"
              className="p-1.5 rounded-lg text-neutral-400 hover:bg-neutral-100 disabled:opacity-30 transition-colors"><ChevronLeft className="w-3.5 h-3.5" /></button>
            <span className="text-[11px] text-neutral-500">{page} / {pageCount}</span>
            <button onClick={() => setPage((p) => Math.min(pageCount, p + 1))} disabled={page >= pageCount} data-testid="rent-page-next"
              className="p-1.5 rounded-lg text-neutral-400 hover:bg-neutral-100 disabled:opacity-30 transition-colors"><ChevronRight className="w-3.5 h-3.5" /></button>
          </div>
        </div>
      </div>

      {/* Graphiques + alertes */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-3 shrink-0 h-56" data-testid="rent-charts-row">
        <div className="bg-white border border-[#E2E8F0] rounded-2xl shadow-sm p-3 min-h-0 flex flex-col" data-testid="rent-evolution">
          <h3 className="font-heading font-bold text-xs mb-1 flex items-center gap-2 shrink-0"><TrendingUp className="w-3.5 h-3.5 text-neutral-400" /> Évolution de la rentabilité</h3>
          <div className="flex-1 min-h-0">
            <SafeChart width="100%" height="100%">
              <ComposedChart data={data?.evolution || []} margin={{ left: -18, right: -14, top: 4, bottom: 0 }}>
                <CartesianGrid vertical={false} stroke="#F1F5F9" />
                <XAxis dataKey="label" tick={{ fontSize: 9 }} />
                <YAxis yAxisId="ca" tick={{ fontSize: 8 }} />
                <YAxis yAxisId="rate" orientation="right" tick={{ fontSize: 8 }} domain={[0, "dataMax + 20"]} />
                <RTooltip formatter={(v, name) => (name === "ca" ? eur(v) : `${v} €/h`)} />
                <Area yAxisId="ca" dataKey="ca" name="ca" stroke="#3B82F6" fill="#DBEAFE" strokeWidth={2} />
                <Line yAxisId="rate" dataKey="rate" name="rate" stroke="#8B5CF6" strokeWidth={2} dot={{ r: 2 }} connectNulls />
              </ComposedChart>
            </SafeChart>
          </div>
          <p className="text-[9px] text-neutral-400 shrink-0 flex gap-3"><span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-[#3B82F6]" /> CA facturé</span><span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-[#8B5CF6]" /> Taux horaire moyen (€/h)</span></p>
        </div>
        <div className="bg-white border border-[#E2E8F0] rounded-2xl shadow-sm p-3 min-h-0 flex flex-col" data-testid="rent-donut">
          <h3 className="font-heading font-bold text-xs mb-1 flex items-center gap-2 shrink-0"><Clock className="w-3.5 h-3.5 text-neutral-400" /> Répartition du temps</h3>
          {(!rep || rep.total_sec === 0) ? (
            <p className="text-[11px] text-neutral-400 text-center my-auto">Aucun temps enregistré sur la période.</p>
          ) : (
            <div className="flex-1 min-h-0 flex items-center gap-2">
              <div className="relative w-28 h-full min-h-24 shrink-0">
                <SafeChart width="100%" height="100%">
                  <PieChart>
                    <Pie data={donut} dataKey="sec" nameKey="label" innerRadius={30} outerRadius={48} paddingAngle={3} strokeWidth={0}>
                      {donut.map((d) => <Cell key={d.id} fill={d.color} />)}
                    </Pie>
                    <RTooltip formatter={(v) => fmtDur(v)} />
                  </PieChart>
                </SafeChart>
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                  <p className="font-heading text-[11px] font-bold" data-testid="rent-donut-total">{fmtDur(rep.total_sec)}</p>
                </div>
              </div>
              <div className="space-y-1 min-w-0">
                {donut.map((d) => (
                  <div key={d.id} className="flex items-center gap-1.5 text-[10px]" data-testid={`rent-donut-${d.id}`}>
                    <span className="w-2 h-2 rounded-full shrink-0" style={{ background: d.color }} />
                    <span className="text-neutral-600 truncate">{d.label}</span>
                    <span className="font-semibold text-neutral-500 ml-auto pl-1 whitespace-nowrap">{fmtDur(d.sec)} · {Math.round(d.sec / rep.total_sec * 100)} %</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
        <div className="bg-white border border-[#E2E8F0] rounded-2xl shadow-sm p-3 min-h-0 flex flex-col" data-testid="rent-alerts">
          <h3 className="font-heading font-bold text-xs mb-1.5 flex items-center gap-2 shrink-0"><AlertTriangle className="w-3.5 h-3.5 text-orange-400" /> Alertes rentabilité</h3>
          <div className="flex-1 min-h-0 overflow-y-auto space-y-2 pr-0.5" data-testid="rent-alerts-list">
            {(data?.alertes || []).length === 0 && (
              <p className="text-[11px] text-neutral-400 py-4 text-center" data-testid="rent-alerts-empty">Aucune alerte — tous les clients sont au-dessus du seuil de {seuil} €/h.</p>
            )}
            {(data?.alertes || []).map((a) => (
              <button key={a.client_id} onClick={() => setDetailId(a.client_id)} data-testid={`rent-alert-${a.client_id}`}
                className="w-full flex items-start gap-2.5 text-left hover:bg-neutral-50 rounded-lg p-1 -m-1 transition-colors">
                <span className={`w-7 h-7 rounded-lg text-[8px] font-bold flex items-center justify-center shrink-0 mt-0.5 ${avatarCls(a.client_name)}`}>{initialsOf(a.client_name)}</span>
                <span className="flex-1 min-w-0">
                  <span className="block text-[11px] font-semibold text-[#0F172A] truncate">{a.client_name}</span>
                  <span className="block text-[9px] text-neutral-400 leading-snug">
                    {a.status === "non_rentable" ? `Taux horaire en dessous du seuil de ${seuil} €/h.` : `Proche du seuil de rentabilité (${seuil} €/h).`}
                    <span className="block text-neutral-500">{a.recommandation}</span>
                  </span>
                </span>
                <span className={`text-[10px] font-bold shrink-0 ${rateColor(a.rate, seuil, watchUntil)}`}>{a.rate != null ? `${Math.round(a.rate)} €/h` : "—"}</span>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Méthode + cartes financières */}
      <div className="grid grid-cols-2 xl:grid-cols-6 gap-3 shrink-0" data-testid="rent-bottom-row">
        <div className="col-span-2 bg-white border border-[#E2E8F0] rounded-2xl shadow-sm p-3" data-testid="rent-methode">
          <h3 className="font-heading font-bold text-xs mb-1 flex items-center gap-2"><Info className="w-3.5 h-3.5 text-neutral-400" /> Méthode de calcul</h3>
          <p className="text-[9px] text-neutral-500 leading-snug">
            Temps total comptabilisé = temps enregistré au chrono (le récurrent contractuel sert de comparaison, sans double comptage).<br />
            Taux horaire réel = montant facturé / temps total · Marge = montant facturé − (temps total × seuil agence).
          </p>
          <p className="text-[9px] font-semibold text-[#B45309] mt-1" data-testid="rent-methode-seuil">Seuil DGA actuel : {seuil} €/h — modifiable dans Paramètres → Rentabilité.</p>
        </div>
        <div className="bg-white border border-[#E2E8F0] rounded-2xl shadow-sm p-3" data-testid="rent-card-facturation">
          <p className="text-[10px] font-medium text-neutral-500 flex items-center gap-1"><FileText className="w-3 h-3" /> Facturation liée</p>
          <p className="font-heading text-lg font-bold mt-1">{eur(data?.facturation?.total || 0)}</p>
          <p className="text-[9px]"><VarBadge value={data?.facturation?.var} /> <span className="text-neutral-400">{data?.facturation?.count ?? 0} facture(s) émise(s)</span></p>
        </div>
        <div className="bg-white border border-[#E2E8F0] rounded-2xl shadow-sm p-3" data-testid="rent-card-attente">
          <p className="text-[10px] font-medium text-neutral-500 flex items-center gap-1"><Wallet className="w-3 h-3" /> Montants en attente</p>
          <p className="font-heading text-lg font-bold mt-1">{eur(data?.attente?.total || 0)}</p>
          <p className="text-[9px]"><VarBadge value={data?.attente?.var} invert /> <span className="text-neutral-400">{data?.attente?.count ?? 0} facture(s) en attente</span></p>
        </div>
        <div className="bg-white border border-[#E2E8F0] rounded-2xl shadow-sm p-3" data-testid="rent-card-marge">
          <p className="text-[10px] font-medium text-neutral-500 flex items-center gap-1"><TrendingUp className="w-3 h-3" /> Marge estimée</p>
          <p className={`font-heading text-lg font-bold mt-1 ${(data?.marge?.total || 0) >= 0 ? "text-emerald-600" : "text-rose-500"}`}>
            {(data?.marge?.total || 0) >= 0 ? "+ " : "− "}{eur(Math.abs(data?.marge?.total || 0))}
          </p>
          <p className="text-[9px] text-neutral-400">{data?.marge?.pct != null ? `${data.marge.pct} % de marge globale` : "—"} (seuil {seuil} €/h)</p>
        </div>
        <div className="bg-white border border-[#E2E8F0] rounded-2xl shadow-sm p-3" data-testid="rent-card-nonvalorise">
          <p className="text-[10px] font-medium text-neutral-500 flex items-center gap-1"><Timer className="w-3 h-3" /> Heures non valorisées</p>
          <p className="font-heading text-lg font-bold mt-1">{fmtDur(data?.non_valorise?.sec || 0)}</p>
          <p className="text-[9px]"><VarBadge value={data?.non_valorise?.var} invert /> <span className="text-neutral-400">Tâches internes, administratif…</span></p>
        </div>
      </div>

      {/* Détail client */}
      <Dialog open={!!detailId} onOpenChange={() => setDetailId(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="rent-detail-modal" aria-describedby={undefined}>
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2.5">
              <span className={`w-8 h-8 rounded-full text-[10px] font-bold flex items-center justify-center ${avatarCls(detail?.client_name || "")}`}>{initialsOf(detail?.client_name || "")}</span>
              {detail?.client_name || "…"}
            </DialogTitle>
          </DialogHeader>
          {detail && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="rent-detail-kpis">
                {[
                  ["Facturation", eur(detail.invoiced)],
                  ["Temps récurrent", detail.recurring_sec ? fmtDur(detail.recurring_sec) : "—"],
                  ["Temps chrono", detail.chrono_sec ? fmtDur(detail.chrono_sec) : "—"],
                  ["Temps total", detail.total_sec ? fmtDur(detail.total_sec) : "—"],
                  ["Taux horaire réel", detail.rate != null ? `${Math.round(detail.rate)} €/h` : "—"],
                  ["Seuil DGA", `${seuil} €/h`],
                  ["Valeur minimum attendue", eur(detail.expected)],
                  ["Marge", `${detail.marge >= 0 ? "+ " : "− "}${eur(Math.abs(detail.marge))}`],
                ].map(([l, v], i) => (
                  <div key={l} className="bg-neutral-50 rounded-xl p-2.5" data-testid={`rent-detail-kpi-${i}`}>
                    <p className="text-[9px] text-neutral-400">{l}</p>
                    <p className="text-sm font-bold font-heading mt-0.5">{v}</p>
                  </div>
                ))}
              </div>
              <div>
                <h4 className="text-xs font-bold mb-1.5">Détail du temps</h4>
                {detail.entries.length === 0 && <p className="text-[11px] text-neutral-400">Aucun temps enregistré sur la période.</p>}
                <div className="space-y-1 max-h-56 overflow-y-auto" data-testid="rent-detail-entries">
                  {detail.entries.map((e) => (
                    <div key={e.id} className="flex items-center gap-2 text-[11px] border-b border-neutral-50 pb-1" data-testid={`rent-detail-entry-${e.id}`}>
                      <span className="text-neutral-400 w-20 shrink-0">{fmtDay(e.date)}</span>
                      <span className="font-medium text-neutral-700 shrink-0">{timeCategoryLabel(e.category)}</span>
                      <span className="text-neutral-400 truncate flex-1">{e.note || ""}</span>
                      <span className="text-neutral-400 shrink-0">{e.user_name}</span>
                      <span className="font-semibold shrink-0">{fmtDur(e.duration_sec)}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div>
                <h4 className="text-xs font-bold mb-1.5">Factures liées</h4>
                {detail.invoices.length === 0 && <p className="text-[11px] text-neutral-400">Aucune facture sur la période.</p>}
                <div className="space-y-1" data-testid="rent-detail-invoices">
                  {detail.invoices.map((i) => (
                    <div key={i.id} className="flex items-center gap-2 text-[11px] border-b border-neutral-50 pb-1" data-testid={`rent-detail-invoice-${i.id}`}>
                      <span className="font-semibold text-[#0F172A] w-24 shrink-0">{i.number || "—"}</span>
                      <span className="text-neutral-400 truncate flex-1">{i.title || ""}</span>
                      <span className="text-neutral-400 shrink-0">{i.issue_date ? fmtDay(i.issue_date) : ""}</span>
                      <span className="font-semibold shrink-0">{eur(i.total_ttc)}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
